import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";

const PANEL_WIDTH = 420;
const PANEL_MAX_HEIGHT = 460;

const LINES: string[] = [
  "WHAT'S STORED",
  "Your public player name, if you set one.",
  "Best score, stars and time per level (to build the leaderboard).",
  "Anonymous gameplay stats - which food you pick, how runs end - never tied to your identity.",
  "",
  "WHAT'S NEVER STORED",
  "No email, phone number or password.",
  "No raw keyboard/swipe input.",
  "No IP address or other personal data, in this game's own data.",
  "",
  "This is a plain-language summary, not a full privacy policy.",
];

/**
 * A short, static "what's stored" panel - the DATA & PRIVACY link from
 * both the ONLINE FEATURES prompt and the Leaderboard screen opens the
 * same one. Deliberately plain text, no legalese, per spec: a product
 * explanation, not a privacy-policy substitute.
 */
export interface DataPrivacyModalHandle {
  container: Phaser.GameObjects.Container;
  setVisible(visible: boolean): void;
  relayout(): void;
  destroy(): void;
}

export function createDataPrivacyModal(scene: Phaser.Scene, depth: number, onClose: () => void): DataPrivacyModalHandle {
  const container = scene.add.container(0, 0).setDepth(depth).setVisible(false);

  const dim = scene.add.rectangle(0, 0, 10, 10, 0x0a0c12, 0.85);
  container.add(dim);

  const panelBg = scene.add.graphics();
  container.add(panelBg);

  const title = scene.add
    .text(0, 0, "DATA & PRIVACY", {
      fontFamily: "Arial, sans-serif",
      fontSize: "20px",
      fontStyle: "bold",
      color: "#f5f7fa",
    })
    .setOrigin(0.5, 0);
  container.add(title);

  const bodyText = scene.add
    .text(0, 0, "", {
      fontFamily: "Arial, sans-serif",
      fontSize: "13px",
      color: "#d7dbe6",
      lineSpacing: 6,
    })
    .setOrigin(0, 0);
  container.add(bodyText);

  const closeButton = createButton(scene, 0, 0, "CLOSE", onClose, {
    width: 160,
    height: 44,
    fillColor: 0x1c2030,
    hoverColor: 0x262c40,
    textColor: "#f5f7fa",
  });
  container.add(closeButton);

  function relayout(): void {
    const { width, height } = scene.scale;
    dim.setSize(width, height).setPosition(width / 2, height / 2);

    const panelWidth = Math.min(PANEL_WIDTH, width * 0.92);
    const panelHeight = Math.min(PANEL_MAX_HEIGHT, height * 0.86);
    const panelX = width / 2 - panelWidth / 2;
    const panelY = height / 2 - panelHeight / 2;

    panelBg.clear();
    panelBg.fillStyle(0x171a24, 1);
    panelBg.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);
    panelBg.lineStyle(1.5, GameConfig.colors.gridLine, 1);
    panelBg.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);

    title.setPosition(width / 2, panelY + 18);

    const innerWidth = panelWidth - 44;
    bodyText.setWordWrapWidth(innerWidth, true);
    bodyText.setText(LINES.join("\n"));
    bodyText.setPosition(panelX + 22, panelY + 60);

    closeButton.setPosition(width / 2, panelY + panelHeight - 34);
  }

  relayout();

  return {
    container,
    setVisible(visible: boolean) {
      container.setVisible(visible);
      if (visible) relayout();
    },
    relayout,
    destroy() {
      container.destroy();
    },
  };
}
